import React, { useEffect, useMemo, useRef, useState } from 'react'
import Breadcrumb from '../Breadcrumb'
import { BigLogo } from '../../../assets/images'
import { logout } from '../../../hooks/CookiesHook'
import { getLocalStorage, homeScreenRedirection } from '../../../utils/helper'
import { Box, Link } from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import WorkspaceSwitcherDrawer from '../WorkspaceSwitcherDrawer'

type Props = {
  breadcrumbData?: any
  page?: string
  parentPage?: string
  disableBreadcrumbLinks?: boolean
  clickableBreadcrumbIndices?: number[]
}

const Header = (props: Props) => {
  const { breadcrumbData, page, parentPage, disableBreadcrumbLinks = false, clickableBreadcrumbIndices } = props

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const workspaceName = getLocalStorage('workspace_name') || ''
  const organizationName = getLocalStorage('organization_name') || ''

  const userInfo = useMemo(() => {
    const info = JSON.parse(`${getLocalStorage('user_info')}`)
    const firstName = info?.firstName || ''
    const lastName = info?.lastName || ''
    return {
      name: `${firstName} ${lastName}`.trim(),
      initials: `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase(),
      image: info?.profilePic as string | null,
      email: getLocalStorage('user_email') || '',
    }
  }, [])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <header className="w-full h-16 border-b-2 border-gray-300 flex items-center px-2 sm:px-6">
      {/* Left side */}
      <Link href={homeScreenRedirection()} className="!no-underline flex md:hidden">
        <img src={BigLogo} alt="nAI" className="h-[29px] object-cover object-left" />
      </Link>
      <Box className="hidden md:flex">
        <Breadcrumb page={page} parentPage={parentPage} breadcrumbData={breadcrumbData} disableLinks={disableBreadcrumbLinks} clickableIndices={clickableBreadcrumbIndices} />
      </Box>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-3">
        {/* Workspace selector */}
        {getLocalStorage('is_bypass') !== 'is_bypass' && (
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex flex-col items-start px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all min-w-[130px] max-w-[220px]"
          >
            <span className="text-xs text-gray-400 leading-none mb-0.5 truncate w-full">Current Subsidiary</span>
            <span className="flex items-center gap-1 w-full">
              <span title={workspaceName || ''} className="text-sm font-semibold text-gray-800 truncate flex-1">
                {workspaceName || ''}
              </span>
              <KeyboardArrowDown style={{ fontSize: 16, color: '#9CA3AF', flexShrink: 0 }} />
            </span>
          </button>
        )}

        {/* User avatar + logout popover */}
        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center justify-center w-9 h-9 rounded-full text-white text-sm font-bold focus:outline-none overflow-hidden"
            style={{ backgroundColor: '#456DB3' }}
          >
            {userInfo.image ? <img src={userInfo.image} alt="avatar" className="w-full h-full object-cover" /> : userInfo.initials || 'U'}
          </button>
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
              {/* User info */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold overflow-hidden"
                  style={{ backgroundColor: '#456DB3' }}
                >
                  {userInfo.image ? <img src={userInfo.image} alt="avatar" className="w-full h-full object-cover" /> : userInfo.initials || 'U'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate" title={userInfo.name || 'User'}>
                    {userInfo.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-400 truncate" title={userInfo.email || organizationName || ''}>
                    {userInfo.email || organizationName || ''}
                  </p>
                </div>
              </div>
              {/* Logout */}
              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  logout()
                }}
                className="w-full text-left px-4 py-3 text-sm text-center font-medium text-red-500 hover:bg-red-50 transition-colors"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <WorkspaceSwitcherDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  )
}

export default Header
